import { TaInput } from '@components/shared';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { SignUpPrompt } from '@features/auth/login';
import { EmailSchema, emailValues, EmailValues } from '@features/auth/registration';
import Cookies from 'js-cookie';

import { AuthHeader } from '@features/auth/AuthHeader';
import { AuthPepper } from '@features/AuthPaper';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { usePageTitle } from '@hooks/usePageTitle';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { FormHelperText } from '@mui/material';
import Button from '@mui/material/Button';
import { useFormik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForgotPassword } from 'services/auth';

export const ResetForm = () => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const { forgotPassword, isLoading, error } = useForgotPassword();
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	usePageTitle(t('resetPassword'));

	const formik = useFormik<EmailValues>({
		initialValues: emailValues,
		validationSchema: EmailSchema,
		onSubmit: async (values: EmailValues) => {
			const response = await forgotPassword(values.email);

			if (response.error) {
				showSnackbar(t('error'), t(response.error), 'error');
			} else {
				Cookies.set('reset_password_email', values.email, { secure: true, expires: 1 });
				navigateWithLanguage(`/login/email-confirmation`);
			}
		},
	});

	const { errors, touched } = formik;

	const renderError = useMemo(() => {
		const hasEmailError = touched.email && errors.email;
		const errorText = errors.email;

		return (
			hasEmailError && (
				<FormHelperText
					sx={{
						display: 'flex',
						marginTop: '10px',
						gap: '9px',
						marginBottom: '4px',
						color: '#FD3333',
						font: 'roboto-regular',
						fontSize: '16px',
					}}
				>
					<ErrorOutlineOutlinedIcon />
					{t(errorText || '')}
				</FormHelperText>
			)
		);
	}, [errors.email, t, touched.email]);

	return (
		<AuthPepper className="bg-[#FFF8F4]">
			<div className="max-w-xxl w-[330px] sm:!w-[448px]">
				<div className="mt-[50px]">
					<AuthHeader
						title={t('resetPassword')}
						spacing="mt-[58px] max-sm:mt-[105px]"
						wrapperSpacing="mb-[18px] max-sm:mb-[20px]"
					/>
				</div>
				<form
					className="max-w-custom-448 mx-auto mb-[80px] flex flex-col gap-[18px]"
					onSubmit={formik.handleSubmit}
				>
					<p className="roboto-regular font-base text-secondary text-base">{t('resetRules')}</p>
					<div>
						<TaInput
							icon={
								<EmailOutlinedIcon
									sx={{
										color: formik.touched.email && formik.errors.email ? '#FD3333' : '#7B809A',
									}}
								/>
							}
							id="email"
							label={t('email')}
							size="small"
							value={formik.values.email}
							onChange={formik.handleChange}
							error={formik.errors.email}
							touched={formik.touched.email}
						/>
						{formik.touched.email && formik.errors.email && (
							<FormHelperText
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '9px',
									marginBottom: '4px',
									color: formik.touched.email && formik.errors.email ? '#FD3333' : '#7B809A',
								}}
							>
								{renderError}
							</FormHelperText>
						)}
					</div>
					<Button
						disabled={isLoading}
						sx={{ font: 'roboto-bold', fontSize: '14px' }}
						type="submit"
						className="w-full text-white"
						variant="contained"
					>
						{t('submit')}
					</Button>
					<div className="mt-[51px]">
						<SignUpPrompt />
					</div>
				</form>
			</div>
		</AuthPepper>
	);
};
